import { Injectable } from '@angular/core';
import { AssetDto, GalleryTheme, Section } from '@nusign/picflow-backend-types';
import { Rect } from '../../classes/rect.class';
import { ScreenSizes } from '../../classes/screen-sizes.class';
import { assetFilenameSpace } from '../../consts';
import { calcColumnCount } from '../../lib/calc-column-count';
import { getAssetVersionDimensions } from '../lib/get-asset-version-dimensions';
import { getGalleryWidth } from '../lib/get-gallery-width';

@Injectable({ providedIn: 'root' })
export class LayoutGridService {
  calculateAssetPositions(
    section: Section,
    theme: GalleryTheme,
    assets: AssetDto[],
    dimensions: {
      galleryWidth: number;
      columnCount: number;
      columnWidth: number;
      spacing: number;
    },
    noOuterSpacing = false
  ): {
    bounds: Rect;
    dimensions: Rect;
  }[] {
    return assets.map((asset, i) =>
      this.calculateAssetPosition(
        section,
        theme,
        asset,
        i,
        dimensions.columnCount,
        dimensions.columnWidth,
        dimensions.spacing,
        noOuterSpacing
      )
    );
  }

  calculateAssetPosition(
    section: Section,
    theme: GalleryTheme,
    asset: AssetDto,
    index: number,
    columnCount: number,
    columnWidth: number,
    spacing: number,
    noOuterSpacing: boolean
  ): {
    bounds: Rect;
    dimensions: Rect;
  } {
    if (ScreenSizes.screenSize() === 'mobile') spacing /= 2;
    const x = index % columnCount;
    const y = Math.floor(index / columnCount);

    const { width, height } = getAssetVersionDimensions(asset.version_data);

    const orientation = width && width > height ? 'horizontal' : 'vertical';
    const bounds = new Rect(
      x * columnWidth,
      y * (columnWidth + ((section.show_filenames ?? theme.section.show_filenames) ? assetFilenameSpace : 0)),
      columnWidth,
      columnWidth
    );

    const dimensions = new Rect(
      Math.round(bounds.x + (noOuterSpacing ? 0 : spacing)),
      Math.round(bounds.y + (noOuterSpacing ? 0 : spacing))
    );

    if (orientation === 'horizontal') {
      dimensions.width = columnWidth - spacing * 2;
      const scale = width ? width / dimensions.width : 1;
      const aspect = (width ? height / (width + spacing * 2 * scale) : 1) || 1;
      dimensions.height = columnWidth * aspect;
      const spacingY = columnWidth - spacing * 2 - dimensions.height;
      dimensions.y += Math.round(spacingY / 2);
    } else {
      dimensions.height = columnWidth - spacing * 2;
      const scale = width ? height / dimensions.height : 1;
      const aspect = (width ? width / (height + spacing * 2 * scale) : 1) || 1;
      dimensions.width = columnWidth * aspect;
      const spacingX = columnWidth - spacing * 2 - dimensions.width;
      dimensions.x += Math.round(spacingX / 2);
    }

    return {
      bounds,
      dimensions,
    };
  }

  public calculateSectionDimensions(
    section: Section,
    theme: GalleryTheme,
    assets: AssetDto[],
    windowWidth: number,
    fullWidth = false,
    maxWidth?: number,
    noOuterSpacing = false
  ) {
    let galleryWidth = getGalleryWidth(section, theme, fullWidth, maxWidth, windowWidth);
    const spacing = section.spacing ?? theme.section.spacing;
    if (noOuterSpacing) {
      if (ScreenSizes.screenSize() === 'mobile') galleryWidth += spacing;
      else galleryWidth += spacing * 2;
    }

    let columnCount = calcColumnCount(section, theme);
    let columnWidth = Math.round(galleryWidth / columnCount);

    if (columnCount > assets.length) {
      columnCount = assets.length;
      galleryWidth = columnWidth * assets.length;
    }

    galleryWidth = Math.round(galleryWidth);
    columnWidth = Math.round(columnWidth);

    return { galleryWidth, columnCount, columnWidth, spacing };
  }

  public getAssetBefore(assetId: string, assets: AssetDto[], assetPositions: { bounds: Rect }[]) {
    const assetIndex = assets.findIndex(a => a.id === assetId);
    if (assetIndex == -1) return;

    const bounds = assetPositions[assetIndex].bounds;
    const targetBounds = new Rect(bounds.x, bounds.y);

    if (targetBounds.x <= 0) return;

    const index = assetPositions.slice(0, assetIndex).findIndex(position => {
      const d = position.bounds;
      if (d.y !== targetBounds.y) return false;
      return d.x + d.width === targetBounds.x;
    });
    return assets[index];
  }

  public getAssetAfter(assetId: string, assets: AssetDto[], assetPositions: { bounds: Rect }[]) {
    const assetIndex = assets.findIndex(a => a.id === assetId);
    if (assetIndex == -1) return;

    const bounds = assetPositions[assetIndex].bounds;
    const targetBounds = new Rect(bounds.x + bounds.width, bounds.y);

    const index = assetPositions.findIndex((position, index) => {
      if (index <= assetIndex) return false; // Skip current and previous assets
      const d = position.bounds;
      if (d.y !== targetBounds.y) return false;
      return d.x === targetBounds.x;
    });
    return assets[index];
  }
}
